import { FC, useState } from "react";
import { Box, CircularProgress, Alert } from "@mui/material";
import {
  GetInsuranceTransactionDto,
  RxBasicInfoDto,
  ExportDataItemDto,
} from "../../../../../../../api-client";
import { useExportedInsuranceDetails } from "../../../../../../../stores/Transactions/transaction.queries";
import { TransactionHeader } from "./TransactionHeader";
import { TicketsPanel } from "./TicketsPanel";
import { MiddlePanel } from "./MiddlePanel";
import { RightDetailPanel } from "./RightDetailPanel";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { de } from "date-fns/locale";
import { TicketsStoreProvider } from "../../../../../../../stores/Ticketing/tickets.provider";

interface ExportModalV2Props {
  transaction: GetInsuranceTransactionDto;
  onClose?: () => void;
}

type SelectedItem =
  | { type: "rx"; data: RxBasicInfoDto }
  | { type: "export"; data: ExportDataItemDto }
  | null;

export const ExportModalV2: FC<ExportModalV2Props> = ({
  transaction,
  onClose,
}) => {
  const { data, isLoading, error } = useExportedInsuranceDetails(
    transaction.id,
  );
  const [selectedItem, setSelectedItem] = useState<SelectedItem>(null);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">
          Fehler beim Laden der Export-Informationen: {error.message}
        </Alert>
      </Box>
    );
  }

  if (!data || (!data.rxs?.length && !data.exportData?.length)) {
    return (
      <Box p={2}>
        <Alert severity="info">
          Keine Export-Informationen verfügbar für diese Transaktion.
        </Alert>
      </Box>
    );
  }

  const handleRxClick = (rx: RxBasicInfoDto) => {
    setSelectedItem({ type: "rx", data: rx });
  };

  const handleExportClick = (exportItem: ExportDataItemDto) => {
    setSelectedItem({ type: "export", data: exportItem });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
      <TicketsStoreProvider>
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
            overflow: "hidden",
          }}
        >
          {/* Transaction and Billing Center Header */}
          <TransactionHeader
            transaction={transaction}
            billingCenter={data.billingCenter}
            onClose={onClose}
          />

          {/* Main Content Area */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "320px 320px 1fr",
              gap: 0.75,
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            {/* Left: Tickets */}
            <TicketsPanel transactionId={transaction.id} />

            {/* Middle: Rx List + Export List */}
            <MiddlePanel
              rxs={data.rxs}
              exportData={data.exportData}
              onRxClick={handleRxClick}
              onExportClick={handleExportClick}
              selectedItem={selectedItem}
              transactionId={transaction.id}
              onClearSelection={() => setSelectedItem(null)}
            />

            {/* Right: Detail Panel */}
            <RightDetailPanel selectedItem={selectedItem} />
          </Box>
        </Box>
      </TicketsStoreProvider>
    </LocalizationProvider>
  );
};
